import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, coincide, formatApiErrorDetail, nivelInfo, NIVELES, ESTADO_PAGO, descargarExcel } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlumnoDialog } from "@/components/AlumnoDialog";
import { AltaExpressForm } from "@/components/AltaExpressForm";
import { SolicitudesNivel } from "@/components/SolicitudesNivel";
import { toast } from "sonner";
import { Plus, Search, ShieldCheck, AlertTriangle, Download, Mail, Loader2, Zap } from "lucide-react";

export default function Alumnos() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [clases, setClases] = useState([]);
  const [padres, setPadres] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [q, setQ] = useState("");
  const [filtroSede, setFiltroSede] = useState("todas");
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [filtroNivel, setFiltroNivel] = useState("todos");
  const [filtroCampamento, setFiltroCampamento] = useState("todos");
  const [open, setOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [expressOpen, setExpressOpen] = useState(false);
  const [invite, setInvite] = useState({ email: "", nombre: "" });
  const [enviando, setEnviando] = useState(false);

  const load = () => api.get("/alumnos").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
    api.get("/sedes").then(({ data }) => setSedes(data));
    api.get("/clases").then(({ data }) => setClases(data));
    api.get("/padres").then(({ data }) => setPadres(data));
    api.get("/planes").then(({ data }) => setPlanes(data.filter((p) => p.activo)));
  }, []);

  const altaExpress = async (datos) => {
    try {
      const { data } = await api.post("/alumnos/alta-express", datos);
      toast.success(`Alumno dado de alta (${data.matricula})`
        + (data.email_enviado ? " · email de consentimiento enviado" : " · el email no salió, reenvíalo desde la ficha"));
      setExpressOpen(false);
      load();
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      return false;
    }
  };

  const enviarInvitacion = async () => {
    setEnviando(true);
    try {
      await api.post("/padres/invitar", invite);
      toast.success(`Email de bienvenida enviado a ${invite.email}`);
      setInviteOpen(false);
      setInvite({ email: "", nombre: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setEnviando(false);
    }
  };

  const sedeName = (id) => sedes.find((s) => s.id === id)?.nombre || "—";
  const padreName = (id) => {
    const p = padres.find((x) => x.id === id);
    return p ? `${p.nombre} ${p.apellidos}`.trim() : "Sin tutor asignado";
  };

  const filtered = useMemo(
    () =>
      items.filter((a) => {
        const t = `${a.nombre} ${a.apellidos} ${a.matricula || ""} ${padreName(a.padre_id)}`;
        if (!coincide(t, q)) return false;
        if (filtroSede !== "todas" && a.sede_id !== filtroSede) return false;
        if (filtroEstado !== "todos" && a.estado !== filtroEstado) return false;
        if (filtroNivel !== "todos" && a.nivel !== filtroNivel) return false;
        if (filtroCampamento === "pendiente"
          && !((a.campamento_incluido || 0) > (a.campamento_usos || []).length)) return false;
        return true;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, q, filtroSede, filtroEstado, filtroNivel, filtroCampamento, padres]
  );

  const create = async (payload, confirmar = false) => {
    try {
      await api.post(`/alumnos${confirmar ? "?confirmar=true" : ""}`, payload);
      toast.success("Alumno creado con matrícula automática");
      setOpen(false);
      load();
    } catch (e) {
      if (e.response?.status === 409) {
        if (window.confirm(formatApiErrorDetail(e.response.data.detail))) return create(payload, true);
        return;
      }
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="alumnos-page">
      <PageHeader
        title="Alumnos"
        subtitle="Matrícula, nivel por color y estado de pago"
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="rounded-full font-semibold" data-testid="export-alumnos-btn"
              onClick={() => descargarExcel("/reportes/alumnos", "alumnos.xlsx")}>
              <Download className="h-4 w-4 mr-2" /> Excel
            </Button>
            <Button variant="outline" className="rounded-full font-semibold" data-testid="alta-express-btn"
              onClick={() => setExpressOpen(true)}>
              <Zap className="h-4 w-4 mr-2" /> Alta express
            </Button>
            <Button variant="outline" className="rounded-full font-semibold" data-testid="enviar-bienvenida-btn"
              onClick={() => setInviteOpen(true)}>
              <Mail className="h-4 w-4 mr-2" /> Enviar email de bienvenida
            </Button>
            <Button className="rounded-full font-semibold transition-transform duration-200 hover:-translate-y-[1px]"
              onClick={() => setOpen(true)} data-testid="add-alumno-btn">
              <Plus className="h-4 w-4 mr-2" /> Nuevo alumno
            </Button>
          </div>
        }
      />

      <SolicitudesNivel onResuelta={load} />

      <div className="flex flex-col sm:flex-row gap-3 mb-7">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9 rounded-lg h-11" placeholder="Buscar por nombre, matrícula o tutor"
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="alumnos-search-input" />
        </div>
        <Select value={filtroSede} onValueChange={setFiltroSede}>
          <SelectTrigger className="sm:w-48 h-11 rounded-lg" data-testid="filter-sede-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas las sedes</SelectItem>
            {sedes.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filtroNivel} onValueChange={setFiltroNivel}>
          <SelectTrigger className="sm:w-44 h-11 rounded-lg" data-testid="filter-nivel-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los niveles</SelectItem>
            {NIVELES.map((n) => <SelectItem key={n.key} value={n.key}>{n.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filtroEstado} onValueChange={setFiltroEstado}>
          <SelectTrigger className="sm:w-40 h-11 rounded-lg" data-testid="filter-estado-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los estados</SelectItem>
            <SelectItem value="activo">Activo</SelectItem>
            <SelectItem value="baja">Baja</SelectItem>
            <SelectItem value="pausado">Pausado</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filtroCampamento} onValueChange={setFiltroCampamento}>
          <SelectTrigger className="sm:w-56 h-11 rounded-lg" data-testid="filter-campamento-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Campamento: todos</SelectItem>
            <SelectItem value="pendiente">Campamento pendiente de usar</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {filtered.map((a) => {
          const nivel = nivelInfo(a.nivel);
          const pago = ESTADO_PAGO[a.estado_pago] || ESTADO_PAGO.pendiente;
          return (
            <Card key={a.id} onClick={() => navigate(`/admin/alumnos/${a.id}`)}
              className="rounded-2xl p-6 border-border shadow-sm cursor-pointer hover:shadow-lg hover:-translate-y-[2px] transition-all duration-300"
              data-testid={`alumno-card-${a.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-display font-bold text-lg leading-tight truncate">{a.nombre} {a.apellidos}</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {a.matricula} · {sedeName(a.sede_id)}
                  </p>
                </div>
                <Badge variant={a.estado === "activo" ? "default" : "secondary"} className="capitalize shrink-0">{a.estado}</Badge>
              </div>
              <div className="mt-4 text-sm text-muted-foreground space-y-1">
                <p>Tutor: {padreName(a.padre_id)}</p>
                <p className="capitalize">Suscripción {a.tipo_suscripcion} · {a.importe} €</p>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-4">
                <Badge variant="outline" className="rounded-full gap-1.5" data-testid={`alumno-nivel-${a.id}`}>
                  <span className={`h-2 w-2 rounded-full ${nivel.dot}`} /> {nivel.label}
                </Badge>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${pago.cls}`}
                  data-testid={`alumno-pago-${a.id}`}>
                  {pago.label}
                </span>
                {a.seguro_reposicion && (
                  <Badge className="rounded-full gap-1"><ShieldCheck className="h-3 w-3" /> Seguro</Badge>
                )}
                {a.gdpr_pendiente && (
                  <Badge variant="outline" className="rounded-full gap-1 text-amber-600 border-amber-500/40">
                    <AlertTriangle className="h-3 w-3" /> Consentimiento pendiente
                  </Badge>
                )}
              </div>
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <p className="text-muted-foreground text-sm" data-testid="alumnos-empty">No hay alumnos que coincidan.</p>
        )}
      </div>

      <AlumnoDialog open={open} onOpenChange={setOpen} sedes={sedes} clases={clases} padres={padres}
        planes={planes} onSave={create} />

      <Dialog open={expressOpen} onOpenChange={setExpressOpen}>
        <DialogContent className="rounded-2xl max-h-[92vh] overflow-y-auto" data-testid="alta-express-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Alta express</DialogTitle>
          </DialogHeader>
          <AltaExpressForm sedes={sedes} clases={clases} planes={planes} onEnviar={altaExpress} />
        </DialogContent>
      </Dialog>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="rounded-2xl" data-testid="invitacion-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Enviar email de bienvenida</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label>Email de la familia</Label>
              <Input type="email" value={invite.email} placeholder="familia@email.com"
                onChange={(e) => setInvite((i) => ({ ...i, email: e.target.value }))}
                data-testid="invitacion-email-input" />
            </div>
            <div className="grid gap-2">
              <Label>Nombre (opcional)</Label>
              <Input value={invite.nombre} placeholder="Nombre del padre/madre"
                onChange={(e) => setInvite((i) => ({ ...i, nombre: e.target.value }))}
                data-testid="invitacion-nombre-input" />
            </div>
            <p className="text-xs text-muted-foreground">
              Recibirá el mensaje de bienvenida con el enlace para completar su registro e
              inscribir a su hijo/a en el portal de familias.
            </p>
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" onClick={enviarInvitacion}
              disabled={enviando || !invite.email} data-testid="invitacion-enviar-btn">
              {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enviar email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
